import { formatDate } from "@luckymenu/core";
import { useTranslation } from "@luckymenu/i18n/react";
import {
	type OrderAlerts,
	useCurrentMembership,
	useLiveConnection,
	useOfflineStatus,
	useOrderAlerts,
	usePrintStation,
	usePush,
	useSoundPermission,
	useSubscriptionNotice,
} from "@luckymenu/staff-core";
import { Button, buttonVariants, cn } from "@luckymenu/ui-web";
import { Link, useRouterState } from "@tanstack/react-router";
import { BellRing, CalendarClock, CloudOff, Volume2, VolumeX, WifiOff } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { ClaimBanners } from "../bill/claim-banners";

/** Keeps the live connection open while a restaurant is ready (shell or paused screen), and makes this browser a print station (P5-T21). */
export function LiveConnection({ children }: { children: ReactNode }) {
	useLiveConnection();
	usePrintStation();
	return <>{children}</>;
}

/** While the alert sounds, the tab title blinks, for a tab in the background. */
function useTitleFlash(alerts: OrderAlerts) {
	const { t } = useTranslation("orders");
	const text = alerts.sound === "newOrder" ? t("alert.titleNewOrder") : t("alert.titleRequest");
	const sounding = alerts.sounding;
	useEffect(() => {
		if (!sounding) return;
		const original = document.title;
		let on = false;
		const timer = setInterval(() => {
			on = !on;
			document.title = on ? `● ${text}` : original;
		}, 1000);
		return () => {
			clearInterval(timer);
			document.title = original;
		};
	}, [sounding, text]);
}

function Bar({
	tone,
	icon,
	children,
	actions,
	label,
}: {
	tone: "alert" | "muted" | "info" | "offline";
	icon: ReactNode;
	children: ReactNode;
	actions?: ReactNode;
	label: string;
}) {
	return (
		<section
			aria-label={label}
			className={cn(
				"mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border px-3 py-2 text-sm",
				tone === "alert" && "border-highlight bg-highlight text-highlight-foreground",
				tone === "muted" && "border-highlight bg-highlight/15",
				tone === "info" && "bg-surface",
				tone === "offline" && "border-warning bg-warning/15",
			)}
		>
			<span className="flex min-w-0 flex-1 basis-56 items-center gap-2 font-medium">
				{icon}
				<span className="min-w-0">{children}</span>
			</span>
			{actions ? <span className="flex shrink-0 gap-2">{actions}</span> : null}
		</section>
	);
}

/**
 * The owner's LuckyMenu subscription banner (P10-T17): payment due, or the
 * last three days of the trial, with View (More > Subscription) and Close
 * (until the app is opened again).
 */
function SubscriptionBanner() {
	const { t } = useTranslation("staff");
	const { notice, close } = useSubscriptionNotice();
	const timeZone = useCurrentMembership().data?.restaurant.timezone;
	const onPage = useRouterState({ select: (s) => s.location.pathname === "/more/subscription" });
	if (!notice) return null;
	const text =
		notice.kind === "pastDue"
			? notice.graceUntil
				? t("subscriptionBanner.pastDueUntil", {
						date: formatDate(new Date(notice.graceUntil), timeZone),
					})
				: t("subscriptionBanner.pastDue")
			: notice.daysLeft === 0
				? t("subscriptionBanner.trialEndsToday")
				: t("subscriptionBanner.trialEnding", { count: notice.daysLeft });
	return (
		<Bar
			tone={notice.kind === "pastDue" ? "offline" : "info"}
			label={text}
			icon={<CalendarClock className="size-4" aria-hidden />}
			actions={
				<>
					{onPage ? null : (
						<Link to="/more/subscription" className={buttonVariants({ size: "sm" })}>
							{t("subscriptionBanner.view")}
						</Link>
					)}
					<Button variant="ghost" size="sm" onClick={close}>
						{t("subscriptionBanner.close")}
					</Button>
				</>
			}
		>
			<span role="status">{text}</span>
		</Bar>
	);
}

/**
 * Across the top of every screen: the offline notice, the request to turn
 * sound on (web browsers need a click first), and the new-order alert with
 * View and Mute. The alert rules are staff-core's `useOrderAlerts`, mounted
 * here once so it sounds on every tab.
 */
export function LiveBanners() {
	const { t } = useTranslation("orders");
	const alerts = useOrderAlerts();
	const sound = useSoundPermission();
	const status = useOfflineStatus();
	const push = usePush();
	const onOrders = useRouterState({ select: (s) => s.location.pathname === "/orders" });
	useTitleFlash(alerts);

	return (
		<div className="sticky top-14 z-20 -mx-4 bg-background px-4 md:top-0 md:-mx-8 md:px-8">
			{status.offline ? (
				<Bar
					tone="offline"
					label={t("alert.offline")}
					icon={<WifiOff className="size-4" aria-hidden />}
				>
					<span role="status" className="grid">
						<span>{t("alert.offline")}</span>
						{status.waiting > 0 ? (
							<span className="text-xs font-normal">
								{t("alert.waiting", { count: status.waiting })}
							</span>
						) : null}
					</span>
				</Bar>
			) : null}
			{status.failed > 0 ? (
				<Bar
					tone="offline"
					label={t("alert.notSentLabel")}
					icon={<CloudOff className="size-4" aria-hidden />}
				>
					<span role="status">{t("alert.failed", { count: status.failed })}</span>
				</Bar>
			) : null}
			{alerts.enabled && !sound.allowed ? (
				<Bar
					tone="info"
					label={t("alert.soundTitle")}
					icon={<Volume2 className="size-4 text-primary" aria-hidden />}
					actions={<Button onClick={() => void sound.allow()}>{t("alert.soundOn")}</Button>}
				>
					<span className="block">{t("alert.soundTitle")}</span>
					<span className="block text-xs font-normal text-muted-foreground">
						{t("alert.soundBody")}
					</span>
				</Bar>
			) : null}
			{push.offer && (!alerts.enabled || sound.allowed) ? (
				<Bar
					tone="info"
					label={t("push.offerTitle")}
					icon={<BellRing className="size-4 text-primary" aria-hidden />}
					actions={
						<>
							<Button variant="ghost" onClick={push.dismiss}>
								{t("push.offerLater")}
							</Button>
							<Button disabled={push.working} onClick={() => void push.enable()}>
								{t("push.offerTurnOn")}
							</Button>
						</>
					}
				>
					<span className="block">{t("push.offerTitle")}</span>
					<span className="block text-xs font-normal text-muted-foreground">
						{push.failed ? t("push.failed") : t("push.offerBody")}
					</span>
				</Bar>
			) : null}
			<SubscriptionBanner />
			<ClaimBanners />
			{alerts.enabled && alerts.waiting ? (
				<Bar
					tone={alerts.sounding ? "alert" : "muted"}
					label={t("alert.label")}
					icon={
						alerts.sounding ? (
							<BellRing className="size-4 animate-pulse motion-reduce:animate-none" aria-hidden />
						) : (
							<VolumeX className="size-4" aria-hidden />
						)
					}
					actions={
						<>
							{onOrders ? null : (
								<Link to="/orders" className={buttonVariants()}>
									{t("alert.view")}
								</Link>
							)}
							{alerts.muted && !alerts.sounding ? (
								<Button variant="outline" onClick={alerts.unmute}>
									{t("alert.unmute")}
								</Button>
							) : (
								<Button variant="outline" onClick={alerts.mute}>
									{t("alert.mute")}
								</Button>
							)}
						</>
					}
				>
					<span role="status" className="flex flex-wrap gap-x-3">
						{alerts.newOrders > 0 ? (
							<span>{t("alert.newOrders", { count: alerts.newOrders })}</span>
						) : null}
						{alerts.requests > 0 ? (
							<span>{t("alert.requests", { count: alerts.requests })}</span>
						) : null}
						{alerts.muted && !alerts.sounding ? (
							<span className="font-normal">{t("alert.muted")}</span>
						) : null}
					</span>
				</Bar>
			) : null}
		</div>
	);
}
